import React, { useState } from 'react';
import { Button, Box, CircularProgress, Typography } from '@mui/material';
import {
  compressImage,
  formatFileSize,
  validateImageType,
  getOptimalCompressionSettings,
} from '../Utils/imageCompression';
import { toast } from 'react-toastify';

const ImageUpload = ({
  onImageUpload,
  acceptedFileTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/heic'],
  label = 'Upload Image',
  avatarSize = 100,
  loading = false,
  maxWidth = 800,
  maxHeight = 800,
  quality = 0.7,
}) => {
  const [imageData, setImageData] = useState(null);
  const [filename, setFilename] = useState('');
  const [originalSize, setOriginalSize] = useState(0);
  const [compressedSize, setCompressedSize] = useState(0);
  const [isCompressing, setIsCompressing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!validateImageType(file, acceptedFileTypes)) {
        toast.error(`Invalid file type. Please upload a valid image`);
        return;
      }

      setFilename(file.name);
      setOriginalSize(file.size);
      setIsCompressing(true);
      
      // Set appropriate processing message for HEIC files
      const isHeic = /\.heic$/i.test(file.name) || file.type === 'image/heic';
      setProcessingMessage(isHeic ? 'Converting HEIC file...' : 'Compressing image...');

      try {
        // Get optimal compression settings based on network type
        const optimalSettings = await getOptimalCompressionSettings();

        // Override with custom settings if provided
        const compressionOptions = {
          maxWidth,
          maxHeight,
          quality,
          ...optimalSettings,
        };

        const result = await compressImage(file, compressionOptions);

        setImageData(result.base64Data);
        setCompressedSize(result.compressedSize);
        setProcessingMessage('');
        
        if (isHeic) {
          toast.success('HEIC file successfully converted and processed!');
        }
      } catch (error) {
        console.error('Image compression failed:', error);
        toast.error(`Failed to process image: ${error.message}`);
        setProcessingMessage('');
      } finally {
        setIsCompressing(false);
      }
    }
  };

  const generatePayload = () => {
    return {
      gift_image: {
        file_str_base64: imageData,
        filename: filename,
      },
    };
  };
  const handleConfirm = () => {
    const payload = generatePayload();
    onImageUpload(payload);
  };

  return (
    <Box
      sx={{
        display: { lg: 'flex' },
      }}
    >
      <Box sx={{ width: { xs: '100%', lg: '70%' } }}>
        <input
          type='file'
          accept={acceptedFileTypes.map(type => type === 'image/heic' ? '.heic,.HEIC' : type).join(', ')}
          onChange={handleImageChange}
          disabled={isCompressing}
          style={{
            textAlign: 'center',
            padding: '6px',
            borderRadius: '10px',
            border: '1px solid #ccc',
            marginBottom: 2,
            width: '100%',
            opacity: isCompressing ? 0.6 : 1,
          }}
        />
        {processingMessage && (
          <Typography variant="body2" color="primary" sx={{ my: 1, textAlign: 'center' }}>
            {processingMessage}
          </Typography>
        )}
      </Box>
      <Button
        variant='outlined'
        color='primary'
        fullWidth
        sx={{ marginLeft: { lg: '10px' }, width: { lg: '30%' } }}
        onClick={handleConfirm}
        disabled={!imageData || loading || isCompressing}
        startIcon={
          loading && (
            <CircularProgress
              sx={{ color: 'primary.main' }}
              size={20}
            />
          )
        }
      >
        {loading ? 'Uploading...' : 'Confirm'}
      </Button>
    </Box>
  );
};

export default ImageUpload;
